<template>
   <!-- WHAT: A component for BtChartForLS to define ID Resolutions (i.e., to obtain names of IDs).
   HISTORY:
   - V260512.1: Added placeholder props to the fields.
   - V260511.1: 1st release (Copied from BtExportFieldDefiner.vue and modified accordingly).
   -->
<div>
   <v-textarea clearable outlined readonly
      class="pt-3 pr-0 mb-0"
      label="ID Resolutions in JSON Format"
      rows="2"
      append-icon="edit"
      v-model="serialized"
      @click:append="editSerialized"
      @click:clear="serializedCleared"
   ></v-textarea>

   <v-dialog no-click-animation persistent scrollable read-only
      max-width="960px"
      v-model="dialog"
   >
      <v-card flat>
         <v-card-title class="title grey--text darken-4 font-weight-bold pb-0">ID Resolution Definitions:</v-card-title>
         <v-card-text class="px-5 pb-5 pt-5">
            <!-- <v-card class="pt-0">
               <v-card-text class="pl-1"> -->
                  <v-form lazy-validation
                     ref="form"
                     v-model="isFormValid"
                  >
                     <v-row v-for="(item, i) in items" :key="i">
                        <v-col xs="12" md="3" class="py-0">
                           <v-autocomplete dense persistent-hint
                              :ref="`dim_${i}`"
                              autocomplete="off"
                              :hint="`Dim ${i+1}`"
                              :items="getDims(i)"
                              v-model="item.dim"
                              @change="dimChanged(item, i)"
                           >
                              <template v-slot:prepend>
                                 <v-icon
                                    class="py-2"
                                    :disabled="!item.dim"
                                    @click="removeItem(i)"
                                 >
                                    delete_forever
                                 </v-icon>
                              </template>
                           </v-autocomplete>
                        </v-col>
                        <template v-if="item.dim">
                           <v-col xs="12" md="2" class="py-0" v-if="item.dim">
                              <v-text-field dense persistent-hint
                                 :ref="`id_${i}`"
                                 autocomplete="off"
                                 placeholder="id field name"
                                 :hint="`${item.id ? 'ID Field Name ' + (i+1) : ''}`"
                                 :disabled="item.systemDim"
                                 :rules="[rules.required]"
                                 v-model="item.id"
                              ></v-text-field>
                           </v-col>
                           <v-col v-if="item.dim"
                              xs="12" md="2" class="py-0"
                           >
                              <v-text-field dense persistent-hint
                                 :ref="`name_${i}`"
                                 autocomplete="off"
                                 placeholder="text field name"
                                 :hint="`${item.name ? 'Text Field Name ' + (i+1) : ''}`"
                                 :disabled="item.systemDim"
                                 :rules="[rules.required]"
                                 v-model="item.name"
                              ></v-text-field>
                           </v-col>
                           <!-- Sample: http://my-service.net/v1/data -->
                           <v-col xs="12" md="5" class="py-0">
                              <v-text-field dense persistent-hint
                                 :ref="`endpoint_${i}`"
                                 autocomplete="off"
                                 placeholder="endpoint"
                                 :hint="`${item.endpoint ? 'Endpoint ' + (i+1) : ''}`"
                                 :disabled="item.systemDim"
                                 :rules="[rules.required, ...(item.systemDim ? [] : [rules.endpoint])]"
                                 v-model="item.endpoint"
                              ></v-text-field>
                           </v-col>
                        </template>
                     </v-row>
                  </v-form>
               <!-- </v-card-text>
            </v-card> -->
         </v-card-text>
         <v-card-actions>
            <div class="flex-grow-1"></div>
            <!-- <v-btn
               text small
               class="mx-0 pl-0 pr-2"
               color="blue darken-1"
               :disabled="items?.length === 1 && !items[0].dim"
               @click="resetClicked"
            >Reset</v-btn> -->
            <v-btn text small
               class="mx-0 px-0"
               color="blue darken-1"
               @click="cancelClicked"
            >Cancel</v-btn>
               <!-- :disabled="isFormValid" -->
            <v-btn
               text small
               class="mx-0 pr-4"
               color="blue darken-1"
               @click="submitClicked"
            >Submit</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>

   <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
   </v-overlay>
</div>
</template>

<script>
const NAME = "BtIdResolutionDefiner";
const _isValidEndpoint = (endpoint) => {
   try {
      const url = new URL(endpoint);
      // Ensure it's not just a valid URL, but one you can call
      return ['http:', 'https:'].includes(url.protocol);
   } catch (err) {
      // If 'new URL()' fails, it's definitely not a valid URL
      return false;
   }
};

class Item {
   constructor(initVal) {
         this.dim = initVal.dim || '';
         this.id = initVal.id || '';
         this.name = initVal.name || '';
         this.endpoint = initVal.endpoint || '';
         this.systemDim = initVal.systemDim || false;
   }
}

export default {
   name: NAME,

   props: {
      /*
      [
         { dim: 'importId', id: '_id', name: 'name', endpoint: '', systemDim: true}
         { dim: 'myIdFld', id: 'id', name: 'label', endpoint: 'http://mfi.com/getIdNames', systemDim: false}
      ]
      */
      value: {
         type: String,
         required: true,
         default: ''
      },
      debug: {
         type: Boolean,
         default: false
      },
      availableItems: {
         type: Array,
         required: true,
         default: () => []
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || 'Value is required!',
            endpoint: value => _isValidEndpoint(value) || 'Must be a valid http/https URL!'
         },
         serialized: '',
         dialog: false,
         isFormValid: true,
         items: [],
         overlay: false
      }
   },

   computed: {
      max() {
         return this.availableItems?.length || 0;
      }
   },

   watch: {
      value: {
         immediate: true,
         handler(val) {
            // alert('in watch.value(): val=' + val);
            this.serialized = val ? val : '';
            this.$forceUpdate();
         }
      }
   },

   methods: {
      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} V260512.1 says => ${msg}`);
            // alert(`${NAME} V260512.1 says:\n${msg}`);
         }
      },

      editSerialized() {
         this.$emit('edit-click', this.serialized);  //V251204
         this.items = this.serialized ? JSON.parse(this.serialized) : [];
         this.addItem();
         this.$nextTick(() => {
            this.dialog = true;
            this.isFormValid = true;
         });
      },

      clearSerialized() {
         this.$emit('input', '');
      },

      dimChanged(item, index) {
         const selectedItem = this.availableItems.find(i => i.dim === item.dim);
         this.$set(this.items, index, new Item(selectedItem))

         if (index >= 0) {
            this.addItem();
            // TODO:
            // const self = this;
            // this.$nextTick(() => {
            //    const ref = item.systemDim ? `dim_${index + 1}` : `id_${index}`;
            //    const fldRef = self.$refs[`${ref}`];
            //    fldRef && fldRef[0].focus();
            // });
         }
      },

      getDims(ind) {
         if (!this.dialog) return [];

         const remainingItems = [];
         this.availableItems.forEach(item => {
            if (
                  item.dim === this.items[ind].dim ||
                  this.items.filter(f => f.dim === item.dim).length === 0
               )
               remainingItems.push(item);
         });

         return remainingItems.map(item => item.dim);
      },

      addItem() {
         const len = this.items.length;
         if (len === 0 || (this.items[len - 1].dim && len < this.max)) {
            this.items.push(new Item({}));
         }
      },

      removeItem(ind) {
         if (confirm('Are you sure?')) {
            this.items.splice(ind, 1);
            this.addItem();
         }
      },

      // resetClicked() {
      //    if (confirm('This action will remove all definitions. Are you sure?')) {
      //       this.items = [];
      //       this.addItem();
      //    }
      // },

      cancelClicked() {
         this.dialog = false;
      },

      submitClicked() {
         this.isFormValid = this.$refs.form.validate();
         if (!this.isFormValid) return;

         const lastItem = this.items[this.items.length - 1];
         const itemsOut = (lastItem && !lastItem.dim) 
            ? this.items.slice(0, -1) 
            : this.items;
         const submitData = itemsOut.length ? JSON.stringify(itemsOut) : '';
         this.log('in submitClicked(): submitData=' + submitData);
         this.$emit('change', submitData);
         this.$emit('input', submitData);
         this.cancelClicked();
      },

      serializedCleared() {
         this.$emit('change', '');
         this.$emit('input', '');
      }
   },

   created() {},

   mounted() {
      // alert(`in ${NAME}.mounted(): `);
   }
}
</script>